import { Link } from "react-router-dom";
import { Card } from "../components/ui/Card";
import {
  FREE_TIER_CREDITS,
  SUBSCRIPTION,
  TOPUP_PACKS,
} from "../../../shared/constants";

// ── Hero Section ──

function Hero() {
  return (
    <section className="px-4 pb-16 pt-20 text-center sm:pb-24 sm:pt-28">
      <h1 className="mx-auto max-w-3xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
        Your First Marketing Hire
      </h1>
      <p className="mx-auto mt-4 max-w-xl text-lg text-slate-500 sm:text-xl">
        Voice in. Content everywhere.{" "}
        <span className="font-semibold text-slate-700">
          ${SUBSCRIPTION.base_price_cents / 100}/month.
        </span>
      </p>
      <div className="mt-8">
        <Link
          to="/signup"
          className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        >
          Start Free
        </Link>
      </div>
    </section>
  );
}

// ── Problem / Solution ──

function ProblemSolution() {
  return (
    <section className="bg-slate-50 px-4 py-16 sm:py-24">
      <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2 md:gap-12">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
            You ship fast. But nobody knows.
          </h2>
          <p className="mt-3 text-base text-slate-500">
            You are building something great, but marketing always falls to the
            bottom of the list. Social posts pile up as drafts. Blog ideas stay
            in your head. Your audience never hears about your progress.
          </p>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-brand-700 sm:text-3xl">
            HelloConvo turns your daily check-in into content on every platform.
          </h2>
          <p className="mt-3 text-base text-slate-500">
            Spend 2 minutes talking about your day. Our AI team handles the
            rest -- platform-optimized posts, your brand voice, published
            everywhere.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── How It Works ──

const STEPS = [
  {
    number: 1,
    title: "Record",
    description:
      "Voice or text check-in about your day. What shipped, what you learned, what is coming next.",
  },
  {
    number: 2,
    title: "Review",
    description:
      "AI generates platform-optimized content in your brand voice. Edit or approve with one click.",
  },
  {
    number: 3,
    title: "Publish",
    description:
      "One click to distribute everywhere -- LinkedIn, Twitter/X, Instagram, and more.",
  },
] as const;

function HowItWorks() {
  return (
    <section className="px-4 py-16 sm:py-24">
      <h2 className="mb-12 text-center text-2xl font-bold text-slate-900 sm:text-3xl">
        How it works
      </h2>
      <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
        {STEPS.map((step) => (
          <div key={step.number} className="text-center">
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-lg font-bold text-white">
              {step.number}
            </span>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              {step.title}
            </h3>
            <p className="mt-2 text-sm text-slate-500">{step.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Pricing ──

const PRICING_TIERS = [
  {
    name: "Free",
    price: "Free",
    description: `${FREE_TIER_CREDITS} credits, no card required`,
    cta: "Get started free",
    ctaLink: "/signup",
    highlight: false,
  },
  {
    name: "Pro",
    price: `$${SUBSCRIPTION.base_price_cents / 100}/mo`,
    description: `${SUBSCRIPTION.included_credits} credits/month`,
    cta: "Subscribe",
    ctaLink: "/signup",
    highlight: true,
  },
  {
    name: "Credits",
    price: `From $${(TOPUP_PACKS[0].price_cents / TOPUP_PACKS[0].credits / 100).toFixed(2)}/credit`,
    description: "Buy as needed",
    cta: "Top up anytime",
    ctaLink: "/signup",
    highlight: false,
  },
] as const;

function Pricing() {
  return (
    <section className="bg-slate-50 px-4 py-16 sm:py-24">
      <h2 className="mb-12 text-center text-2xl font-bold text-slate-900 sm:text-3xl">
        Simple pricing
      </h2>
      <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
        {PRICING_TIERS.map((tier) => (
          <Card
            key={tier.name}
            className={
              tier.highlight
                ? "ring-2 ring-brand-600"
                : undefined
            }
          >
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-900">
                {tier.name}
              </h3>
              <p className="mt-2 text-3xl font-bold text-slate-900">
                {tier.price}
              </p>
              <p className="mt-1 text-sm text-slate-500">{tier.description}</p>
              <Link
                to={tier.ctaLink}
                className={`mt-6 inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  tier.highlight
                    ? "bg-brand-600 text-white hover:bg-brand-700"
                    : "bg-slate-100 text-slate-900 hover:bg-slate-200"
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ── Social Proof Placeholder ──

function SocialProof() {
  return (
    <section className="px-4 py-16 text-center sm:py-24">
      <p className="text-sm font-medium uppercase tracking-wide text-slate-400">
        Trusted by indie builders and small teams
      </p>
      <div className="mx-auto mt-6 flex max-w-xs justify-center gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-10 w-10 rounded-full bg-slate-200"
            aria-label="User avatar placeholder"
          />
        ))}
      </div>
    </section>
  );
}

// ── Footer ──

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white px-4 py-8">
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <nav className="flex gap-6 text-sm text-slate-500">
          <a href="#how-it-works" className="hover:text-slate-700">
            Product
          </a>
          <a href="#pricing" className="hover:text-slate-700">
            Pricing
          </a>
          <Link to="/login" className="hover:text-slate-700">
            Login
          </Link>
          <Link to="/signup" className="hover:text-slate-700">
            Sign Up
          </Link>
        </nav>
        <p className="text-xs text-slate-400">
          &copy; {new Date().getFullYear()} HelloConvo. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

// ── Landing Page ──

export function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Hero />
      <ProblemSolution />
      <div id="how-it-works">
        <HowItWorks />
      </div>
      <div id="pricing">
        <Pricing />
      </div>
      <SocialProof />
      <Footer />
    </div>
  );
}
